import { useEffect } from 'react';
import { Patient, RelatedPerson } from '@medplum/fhirtypes';
import { UseFormReturnType } from '@mantine/form';
import {
  getFormValuesFromPatient,
  getFormValuesFromRelatedPerson,
} from '@utils/create-patient-helpers';
import { HL7 } from '@/consts/systems.consts';

export type RelationType = 'father' | 'mother' | 'spouse';
export const RELATION_TYPES: RelationType[] = ['father', 'mother', 'spouse'];

interface UsePatientFormInitializationParams {
  patient: Patient | null;
  existingRelatedPeople: RelatedPerson[];
  form: UseFormReturnType<any>;
  setRelatedPersons: (relatedPersons: Partial<Record<RelationType, RelatedPerson>>) => void;
  setInitialFormValues: (values: any) => void;
  opened?: boolean;
}

export const usePatientFormInitialization = ({
  patient,
  existingRelatedPeople,
  form,
  setRelatedPersons,
  setInitialFormValues,
 opened = true,
}: UsePatientFormInitializationParams) => {
  useEffect(() => {
    // Only initialize when modal is open
    if (!opened) {
      return;
    }

    if (!patient) {
      form.reset();
      setInitialFormValues(null);
      return;
    }

    const patientValues = getFormValuesFromPatient(patient);
    form.setValues(patientValues);

    const results = RELATION_TYPES.map((type) => {
      const relativePatient = existingRelatedPeople.find(
        (rp) =>
          rp.relationship?.[0]?.coding?.[0]?.code ===
          (type === 'father'
            ? HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.FATHER
            : type === 'mother'
              ? HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER
              : HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.SPOUSE)
      ) as RelatedPerson | undefined;
      return { type, relativePatient };
    });

    const newRelatedPersons: Partial<Record<RelationType, RelatedPerson>> = {};
    const mergedFormValues = results.reduce(
      (acc, { type, relativePatient }) => {
        if (relativePatient) {
          newRelatedPersons[type] = relativePatient;
          return {
            ...acc,
            ...getFormValuesFromRelatedPerson(relativePatient, type),
          };
        }
        return acc;
      },
      {}
    );

    const finalValues = {
      ...patientValues,
      ...mergedFormValues,
    };

    form.setValues(finalValues);
    setRelatedPersons(newRelatedPersons);
    setInitialFormValues(finalValues);
    form.resetDirty();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, existingRelatedPeople, opened]);
};
